import { useEffect, useState } from "react";
import axios from "axios";
import { Users, Download, RefreshCw } from "lucide-react";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/waitlist`);
      setEntries(res.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const exportCsv = () => {
    const rows = [
      ["Phone", "City", "Joined At"],
      ...entries.map((e) => [
        e.phone,
        e.city,
        new Date(e.created_at).toLocaleString(),
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "medicines-now-waitlist.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
        <div className="flex items-center justify-between">
          <Logo size="sm" />
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-button"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition-[transform] hover:scale-[1.03]"
              style={{ backgroundColor: "var(--action-green)" }}
            >
              <Download size={15} /> Export CSV
            </button>
          </div>
        </div>

        <div className="mt-10 flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div
            className="grid h-12 w-12 place-items-center rounded-xl text-white"
            style={{ backgroundColor: "var(--trust-blue)" }}
          >
            <Users size={22} />
          </div>
          <div>
            <p className="text-sm text-slate-500">Total waitlist signups</p>
            <p
              className="font-display text-3xl font-extrabold text-slate-900"
              data-testid="admin-total-count"
            >
              {entries.length}
            </p>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <table className="w-full text-left text-sm" data-testid="admin-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-6 py-4">#</th>
                <th className="px-6 py-4">Phone</th>
                <th className="px-6 py-4">City</th>
                <th className="px-6 py-4">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-slate-400">
                    Loading…
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-slate-400">
                    No signups yet.
                  </td>
                </tr>
              ) : (
                entries.map((e, i) => (
                  <tr key={e.id} className="hover:bg-slate-50/60">
                    <td className="px-6 py-4 text-slate-400">{i + 1}</td>
                    <td className="px-6 py-4 font-medium text-slate-800">
                      +91 {e.phone}
                    </td>
                    <td className="px-6 py-4 text-slate-600">{e.city}</td>
                    <td className="px-6 py-4 text-slate-500">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
